import { useRef, type ReactNode } from 'react';

import { Box, SwipeableDrawer, type SxProps, type Theme } from '@mui/material';

import { useBottomSheetHeight } from 'hooks/useBottomSheetHeight';

const DEFAULT_TRANSITION_MS = 260;
export const DEFAULT_MIN_HEIGHT = 200;
export const DEFAULT_MAX_RATIO = 0.7;

type BottomSheet = {
    open: boolean;
    onOpen: () => void;
    onClose: () => void;
    children: ReactNode;
    title: ReactNode;
    measureElements?: (HTMLElement | null)[] /** Elementos usados para calcular a altura animada do bottom sheet. Omitir = altura natural respeitando min/max. */;
    minHeight?: number /** Altura mínima (px) da área de conteúdo. */;
    maxRatio?: number /** Proporção máxima da altura da viewport que a área de conteúdo pode ocupar. */;
    transitionMs?: number;
    swipeAreaWidth?: number;
    paperSx?: SxProps<Theme>;
    bodySx?: SxProps<Theme>;
};

export function BottomSheet({
    open,
    onOpen,
    onClose,
    children,
    title,
    measureElements,
    minHeight = DEFAULT_MIN_HEIGHT,
    maxRatio = DEFAULT_MAX_RATIO,
    transitionMs = DEFAULT_TRANSITION_MS,
    paperSx,
    bodySx,
}: BottomSheet) {
    const bodyRef = useRef<HTMLDivElement>(null);
    const animatedHeight = useBottomSheetHeight(bodyRef, measureElements, minHeight, maxRatio);
    const useAnimated = measureElements !== undefined;

    return (
        <SwipeableDrawer
            anchor="bottom"
            open={open}
            onOpen={onOpen}
            onClose={onClose}
            disableSwipeToOpen={true}
            ModalProps={{ disableAutoFocus: true }}
            slotProps={{
                paper: {
                    sx: {
                        borderTopLeftRadius: 12,
                        borderTopRightRadius: 12,
                        backgroundColor: 'secondary.main',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        ...paperSx,
                    },
                },
            }}
        >
            <Box sx={{ color: 'common.white', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1, pb: 0.5 }}>
                    <Box sx={{ width: 48, height: 4, borderRadius: 2, backgroundColor: 'rgba(255, 255, 255, 0.4)' }} />
                </Box>

                <Box sx={{ px: 2, pb: 1.5, fontSize: 22, fontWeight: 300, lineHeight: 1.1, display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                    {title}
                </Box>
            </Box>

            <Box
                ref={bodyRef}
                sx={{
                    backgroundColor: 'common.white',
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                    p: 2,
                    display: 'flex',
                    gap: 2,
                    flexDirection: 'column',
                    minHeight: `${minHeight}px`,
                    maxHeight: `${maxRatio * 100}dvh`,
                    overflow: 'auto',
                    ...(useAnimated && {
                        height: animatedHeight,
                        overflow: 'hidden',
                        transition: `height ${transitionMs}ms cubic-bezier(0.4,0,0.2,1)`,
                        willChange: 'height',
                    }),
                    ...bodySx,
                }}
            >
                {children}
            </Box>
        </SwipeableDrawer>
    );
}
